import { useLocation } from 'react-router-dom';
import { useDiscoveryMode, useDiscoveryOptional } from '../contexts/discoveryStore';
import { discoveryHomeUrl, nextDiscoveryUrl } from '../utils/discoveryFilters';

/**
 * Tab/CTA navigation targets (T013-FE, canonical territories plan).
 *
 * In `v2` every discovery-surface navigation entry (logo, Home/Esplora tabs,
 * hero CTAs, weekend CTAs, empty-state CTAs, category chips/tiles, close
 * fallbacks) builds its target through the shared patch builder
 * (`nextDiscoveryUrl`/`discoveryHomeUrl`) from the CURRENT URL, so:
 * - a territorial URL stays anchored to its territory (plan l.62/l.64);
 * - the Esplora surface carries filters/radius/view/mapBbox/tracking;
 * - the Home link drops filters and map presentation, keeping tracking;
 * - a path category becomes `?categorie=` when moving to Esplora.
 * In `legacy`/`loading` every value equals today's literal target, so the
 * pre-activation navigation stays HEAD-equivalent.
 *
 * `surface` is the active-tab classification of the current URL (plan l.146:
 * active tabs recognize territorial Home/Esplora/category/map). It reads the
 * discovery descriptor (the normalized URL authority), never a second parser.
 */
export type DiscoveryTabSurface = 'home' | 'esplora' | 'other';

export interface DiscoveryNav {
  /** True only when the discovery URL authority is live. */
  v2: boolean;
  /** Home tab/logo target — territorial Home in v2, `/` in legacy. */
  homeUrl: string;
  /** Esplora tab target — territorial Esplora (query carried) in v2. */
  esploraUrl: string;
  /** Which tab surface the current URL belongs to (v2 active-tab rules). */
  surface: DiscoveryTabSurface;
  /**
   * Weekend CTA target: the Esplora surface with the explicit dates (plan
   * l.147 — the CTA preserves its dates). Legacy keeps the generic
   * `/esplora?startDate=…&endDate=…` string.
   */
  weekendUrl: (startDate: string, endDate: string) => string;
  /**
   * Category chip/tile href for the current URL via the shared builder in v2;
   * `null` in legacy (the caller keeps `categoryHref`, the legacy builder).
   */
  categoryUrl: (categoryId: string) => string | null;
}

function surfaceOfRouteKind(kind: string | undefined): DiscoveryTabSurface {
  if (kind === 'territory-home' || kind === 'generic-home') return 'home';
  // A period page IS the territory Home with a window — the Home tab stays
  // the active surface (no separate period tab exists).
  if (kind === 'territory-period') return 'home';
  if (
    kind === 'territory-esplora' ||
    kind === 'territory-category' ||
    kind === 'generic-esplora'
  ) {
    // Territory + map view normalizes to the territorial Esplora surface, so
    // free-map URLs are already covered by these kinds (plan l.146).
    return 'esplora';
  }
  return 'other';
}

export function useDiscoveryNav(): DiscoveryNav {
  const location = useLocation();
  const v2 = useDiscoveryMode() === 'v2';
  const discovery = useDiscoveryOptional();
  const current = { pathname: location.pathname, search: location.search };

  const homeUrl = v2 ? discoveryHomeUrl(current) ?? '/' : '/';
  const esploraUrl = v2 ? nextDiscoveryUrl(current, { surface: 'esplora' }) ?? '/esplora' : '/esplora';
  // Legacy has no discovery descriptor: no tab surface applies.
  const surface = v2 ? surfaceOfRouteKind(discovery?.descriptor?.route.kind) : 'other';

  const weekendUrl = (startDate: string, endDate: string) => {
    const target = v2 ? nextDiscoveryUrl(current, { surface: 'esplora', startDate, endDate }) : null;
    return target || `/esplora?startDate=${startDate}&endDate=${endDate}`;
  };

  const categoryUrl = (categoryId: string) =>
    v2 ? nextDiscoveryUrl(current, { categoryIds: [categoryId] }) : null;

  return { v2, homeUrl, esploraUrl, surface, weekendUrl, categoryUrl };
}
